// @autogenerated

// WARNING THIS FILE IS AUTOGENERATED! DO NOT EDIT!

// Developer note: these files are treated as templates and called from prebuild.js
// They shouldn't be imported and used directly.

import React from 'react';
import { Link } from 'gatsby';
import onChangeGenerator from '../../commons/onChangeGenerator';
import P from '@govtnz/ds/build/react-ts/P';
import '../../commons/styles/ds/themed-P.scss';
import H2 from '@govtnz/ds/build/react-ts/H2';
import '../../commons/styles/ds/themed-H2.scss';
import Ul from '@govtnz/ds/build/react-ts/Ul';
import '../../commons/styles/ds/themed-Ul.scss';
import Li from '@govtnz/ds/build/react-ts/Li';
import '../../commons/styles/ds/themed-Li.scss';
import A from '@govtnz/ds/build/react-ts/A';
import '../../commons/styles/ds/themed-A.scss';
import components__Button from '../../commons/examples/components__Button';
// Indirect relative import because this template is output to src/pages/components so it needs to step back to `commons`.
import ComponentPage from '../../commons/component-page';
import ComponentCode from '../../commons/component-code';
import Example from '../../commons/Example';
import ExampleContainer from '../../commons/ExampleContainer';
import ExampleHeading from '../../commons/ExampleHeading';
import ExampleSection from '../../commons/ExampleSection';
import MainNavMobileMenuContext from '../../commons/MainNavMobileMenuContext';
import '../../commons/styles/ds/themed-Button.scss';
import '../../commons/styles/ds/themed-CaptionL.scss';

const PageContent = (props) => <React.Fragment><P styleSize="large">
    Buttons make important actions stand out within a task flow, and help users
    to take those actions easily. Use colour and text in buttons to inform users about what will happen when they activate a button.
</P>

<H2 styleSize="large" id="when-to-use-it">When to use it</H2>
<P>Use buttons to help users carry out key actions in a task flow, like starting
an application or saving their information.</P>
<P>If you are considering using an icon in a button, we suggest you use visible
text as well to ensure all users understand the button&#39;s function.</P>
<h2 id="how-it-works">How it works</h2>
<P>Keep button text short. Start with a verb that clearly describes the action
the button performs. Write button text in sentence case.</P>
<P>Some typical examples of button text are:</P>
<Ul>
<Li>&#39;Start now&#39; at the start of the service</Li>
<Li>&#39;Sign in&#39; to an account a user has already created</Li>
<Li>&#39;Continue&#39; when the service does not save a user&#39;s information</Li>
<Li>&#39;Save and continue&#39; when the service does save a user&#39;s information</Li>
<Li>&#39;Save and return later&#39; when a user can save their information and come
back to it later</Li>
<Li>&#39;Pay&#39; to make a payment</Li>
<Li>&#39;Confirm and send&#39; on a check answers page that does not have any legal
content a user must agree to</Li>
<Li>&#39;Accept and send&#39; on a check answers page that has legal content a user
must agree to</Li>
<Li>&#39;Sign out&#39; when a user is leaving an account that they signed in to</Li>
<Li>&#39;Add another [name of thing]&#39; to add another item to a list or group. Ensure
the button text describes clearly what this button will add, in case there are
other similar buttons on the same page or form.</Li>
</Ul>
<P>Consider if you need to include more words to clearly describe a button&#39;s
action. For example, &#39;Add another address&#39; or &#39;Accept and claim a tax refund&#39;.</P>
<h2 id="primary-buttons">Primary buttons</h2>
<P>Use a primary button for the main &#39;call to action&#39; on a page, such
as &#39;Save and send&#39;.</P>
<P>Avoid using several primary buttons on a single page. Having more than one
&#39;call to action&#39; reduces its impact, and makes it harder for users to know
what to do next.</P>
<ExampleContainer>
    <ExampleHeading>Primary buttons</ExampleHeading>
    <Example  code={components__Button[0]} iframeProps={{
        id:"iframe_componentsButton0",
        className: "example__iframe",
        src:"/components/Button__example0.html",
        title:"Example: Primary buttons",
        height: 200
      }}></Example>
</ExampleContainer>

<h2 id="secondary-buttons">Secondary buttons</h2>
<P>Use secondary buttons for alternative or intermediary actions on a page.
Pages with too many calls to action make it hard for users to know what to do
next. Before adding a secondary button, try to simplify the page or break the
content down into several pages.</P>
<ExampleContainer>
    <ExampleHeading>Secondary buttons</ExampleHeading>
    <Example  code={components__Button[1]} iframeProps={{
        id:"iframe_componentsButton1",
        className: "example__iframe",
        src:"/components/Button__example1.html",
        title:"Example: Secondary buttons",
        height: 200
      }}></Example>
</ExampleContainer>

<P>You can use secondary buttons in combination with a primary button, such as
offering &#39;Cancel&#39; or &#39;Save and return later&#39; options. Consider limiting
combinations to a maximum of two secondary buttons with a single primary
button, as more can confuse users.</P>
<ExampleContainer>
    <ExampleHeading>Primary and secondary combination</ExampleHeading>
    <Example  code={components__Button[2]} iframeProps={{
        id:"iframe_componentsButton2",
        className: "example__iframe",
        src:"/components/Button__example2.html",
        title:"Example: Primary and secondary combination (Button)",
        height: 200
      }}></Example>
</ExampleContainer>

<h2 id="warning-buttons">Warning buttons</h2>
<P>Warning buttons are designed to make users think carefully before they use
them. Warning buttons only work well if used very sparingly..</P>
<P>Only use warning buttons for actions with serious destructive consequences that
cannot be easily undone by a user, such as permanently deleting an account.</P>
<P>When letting users carry out an action like this, it&#39;s a good idea to include
an extra first step that asks users to confirm they want to do
the destructive action.</P>
<P>In this instance, use one warning button for the initial call to action, and
another warning button for the final confirmation.</P>
<P>Do not only rely solely on the red colour of a warning button to communicate
the serious nature of the action. Not all users will be able to see the colour
or may not understand what it signifies. Make sure the context and button text
make clear what will happen if the user selects it.</P>
<ExampleContainer>
    <ExampleHeading>Warning buttons</ExampleHeading>
    <Example  code={components__Button[3]} iframeProps={{
        id:"iframe_componentsButton3",
        className: "example__iframe",
        src:"/components/Button__example3.html",
        title:"Example: Warning buttons",
        height: 200
      }}></Example>
</ExampleContainer>

<h2 id="disabled-buttons">Disabled buttons</h2>
<P>Disabled buttons, which are often greyed out, have poor contrast and can
confuse some users, so avoid them if possible. Disabled buttons may also
impede keyboard accessibility.</P>
<P>Only use disabled buttons if your research shows it makes the user interface
easier to understand.</P>
<ExampleContainer>
    <ExampleHeading>Disabled buttons</ExampleHeading>
    <Example  code={components__Button[4]} iframeProps={{
        id:"iframe_componentsButton4",
        className: "example__iframe",
        src:"/components/Button__example4.html",
        title:"Example: Disabled buttons",
        height: 200
      }}></Example>
</ExampleContainer>

<h2 id="stop-users-from-accidentally-sending-information-twice">Stop users from accidentally sending information twice</h2>
<P>You can prevent users from accidentally sending information more than once by
preventing multiple clicks from registering.</P>
<P>Sometimes, users will click buttons more than once because they:</P>
<Ul>
<Li>have used operating systems where they have to rapidly click twice on items
to make them work</Li>
<Li>are experiencing a slow connection, which means they are not given feedback
on their action quickly enough</Li>
<Li>have motor impairments, such as hand tremors, that cause them to click
the button involuntarily.</Li>
</Ul>
<P>In some cases, multiple clicks can mean users&#39; information is sent more than
once. For example, a notify team found that a number of users were receiving
invitations twice, because the person sending them was clicking twice on
the &#39;Send&#39; button.</P>
<P>If you are working in production and your research shows that users are
frequently sending information twice, consider using features that configure
the button to ignore a rapid second click. If the cause is a slow connection,
give the user a &#39;loading spinner&#39; to show progress.</P>
<h2 id="credit">Credit</h2>
<P>Guidance, original HTML and CSS derived from&nbsp;
<A className="g-link"  href="https://github.com/alphagov/govuk-frontend">GOV.UK Design System</A>.</P>
</React.Fragment>

export default function Code(props) {
  return (
    <ComponentPage
      title={"Button"}
      id={"Button"}
      pageProps={props}
      PageContent={PageContent}
    />
  );
}
